import React from 'react';
import { TextField, Chip, Tooltip, Switch, Box } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

/**
 * Get editable properties from schema (exclude id and _id).
 * Handles:
 *   - array schema:  { type: 'array', items: { properties: {...} } }
 *   - object schema: { type: 'object', properties: {...} }
 *   - flat x-control schema: { fieldA: { 'x-control': ... }, fieldB: ... }
 */
export const getEditableCollectionProperties = (schema: Record<string, any>) => {
  const SYSTEM_KEYS = new Set(['$schema', 'type', 'title', 'description', 'required', 'additionalProperties', 'items']);

  let properties = schema?.items?.properties || schema?.properties || null;

  // Handle flat schema where properties are defined directly with x-control
  if (!properties) {
    const flatEntries = Object.entries(schema || {}).filter(
      ([key, val]) => !SYSTEM_KEYS.has(key) && val && typeof val === 'object' && (val as any)['x-control']
    );
    if (flatEntries.length > 0) {
      properties = Object.fromEntries(flatEntries);
    } else {
      properties = {};
    }
  }

  const editable: Record<string, any> = {};
  Object.keys(properties).forEach(key => {
    if (key !== 'id' && key !== '_id') {
      editable[key] = properties[key];
    }
  });
  return editable;
};

/**
 * Get all properties from collection schema
 */
export const getCollectionSchemaProperties = (
  schemeMap: Record<string, any>,
  schemaKey: string | null
): Record<string, any> => {
  if (!schemaKey || !schemeMap[schemaKey]) {
    return {};
  }
  const schema = schemeMap[schemaKey];
  return schema.schemaDefinition?.items?.properties || {};
};

/**
 * Get field schema definition
 */
export const getFieldSchema = (
  properties: Record<string, any>,
  fieldKey: string
): Record<string, any> => {
  return properties[fieldKey] || {};
};

/**
 * Get field display title
 */
export const getFieldTitle = (
  properties: Record<string, any>,
  fieldKey: string
): string => {
  const fieldSchema = getFieldSchema(properties, fieldKey);
  return fieldSchema['x-control']?.label || fieldSchema.title || fieldKey;
};

/**
 * Get lookup configuration for a field from x-control.settings
 */
export const getLookupConfig = (
  properties: Record<string, any>,
  fieldKey: string
): any => {
  const fieldSchema = getFieldSchema(properties, fieldKey);
  const xControl = fieldSchema['x-control'];
  if (xControl?.type === 'lookup' && xControl?.settings?.source) {
    return xControl.settings;
  }
  return null;
};

/**
 * Render lookup cell value as React chips
 * lookupConfig = x-control.settings: { source, keyProp, renderProp, multiselect }
 * cachedData = { [itemId]: itemData } from lookupCache[source]
 */
export const renderLookupCellValue = (
  lookupConfig: any,
  itemId: string | string[] | undefined,
  cacheForSource: Record<string, any> | undefined
): React.ReactNode => {
  const renderProp = lookupConfig.renderProp || 'name';

  const renderChip = (id: string) => {
    const cached = cacheForSource?.[id];
    const displayLabel = cached?.[renderProp] || String(id).substring(0, 8) + '...';
    return (
      <Chip
        key={id}
        label={displayLabel}
        size="small"
        variant="outlined"
        sx={{ fontSize: '11px', height: '20px', mr: 0.5, mb: 0.25 }}
      />
    );
  };

  if (Array.isArray(itemId)) {
    if (itemId.length === 0) return <span>-</span>;
    return <>{itemId.map((id) => renderChip(id))}</>;
  }

  if (!itemId) return <span>-</span>;
  return renderChip(itemId);
};

/** Inline-editable control types in collection table cells */
const INLINE_EDITABLE_CONTROLS = new Set(['string', 'textarea', 'number', 'date', 'datetime', 'richtext']);

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']);

const isImageUrl = (url: string): boolean => {
  try {
    const ext = url.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
    return IMAGE_EXTENSIONS.has(ext) || url.includes('/uploads/') || /\/api\/media\/[^/]+\/[^/]+$/.test(url);
  } catch {
    return false;
  }
};

/**
 * Render appropriate inline input for a collection table cell based on x-control.
 * - string, textarea → text input
 * - number          → number input
 * - date            → date input
 * - datetime        → datetime-local input
 * - richtext        → plain text input (HTML stripped)
 * - others          → "Support later" chip (read-only)
 */
export const renderCollectionCellInput = (
  fieldSchema: Record<string, any>,
  value: any,
  onChange: (v: any) => void
): React.ReactNode => {
  const xControl = fieldSchema['x-control'];
  const controlType: string = xControl?.type ?? '';

  const cellInputSx = { '& input': { py: 0.5, px: 0.75 } };

  switch (controlType) {
    case 'string':
    case 'textarea':
      return (
        <TextField
          size="small"
          value={typeof value === 'string' ? value : (value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          sx={cellInputSx}
        />
      );

    case 'number':
      return (
        <TextField
          type="number"
          size="small"
          value={value ?? ''}
          onChange={(e) => {
            const num = parseFloat(e.target.value);
            onChange(isNaN(num) ? '' : num);
          }}
          fullWidth
          sx={cellInputSx}
        />
      );

    case 'date': {
      const dateVal = value ? String(value).slice(0, 10) : '';
      return (
        <TextField
          type="date"
          size="small"
          value={dateVal}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={cellInputSx}
        />
      );
    }

    case 'datetime': {
      const dtVal = value ? new Date(value).toISOString().slice(0, 16) : '';
      return (
        <TextField
          type="datetime-local"
          size="small"
          value={dtVal}
          onChange={(e) => {
            onChange(e.target.value ? new Date(e.target.value).toISOString() : '');
          }}
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={cellInputSx}
        />
      );
    }

    case 'richtext': {
      // Strip HTML tags for simple inline editing
      const plainText = typeof value === 'string' ? value.replace(/<[^>]*>/g, '') : (value ?? '');
      return (
        <TextField
          size="small"
          value={plainText}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          placeholder="Plain text (rich formatting via modal)"
          sx={cellInputSx}
        />
      );
    }

    case 'boolean':
    case 'toggle': {
      const boolVal = value === true || value === 'true' || value === 1;
      return (
        <Switch
          checked={boolVal}
          onChange={(e) => onChange(e.target.checked)}
          size="small"
          color="primary"
        />
      );
    }

    default:
      return (
        <Tooltip title={controlType ? `"${controlType}" control — edit via modal` : 'Edit via modal'}>
          <Chip label="Support later" size="small" variant="outlined" sx={{ fontSize: '11px' }} />
        </Tooltip>
      );
  }
};

/**
 * Render a read-only cell value based on x-control type.
 * - string / textarea / number / date / datetime → display as string
 * - richtext  → strip HTML, truncate
 * - lookup    → use lookup cache (handled by caller)
 * - others    → "Support later" chip
 */
export const renderCollectionCellDisplay = (
  fieldSchema: Record<string, any>,
  cellValue: any,
): React.ReactNode => {
  const xControl = fieldSchema['x-control'];
  const controlType: string = xControl?.type ?? '';

  if (controlType === 'datetime') {
    if (cellValue === null || cellValue === undefined || cellValue === '') return '-';
    try {
      const d = new Date(cellValue);
      if (isNaN(d.getTime())) return String(cellValue);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: '12px', px: 1.25, py: 0.25, fontSize: '0.8rem', fontWeight: 500 }}>
          <AccessTimeIcon sx={{ fontSize: '13px' }} />
          {`${dd}-${mm}-${yyyy} ${hh}:${min}`}
        </Box>
      );
    } catch {
      return String(cellValue);
    }
  }

  if (controlType === 'date') {
    if (cellValue === null || cellValue === undefined || cellValue === '') return '-';
    try {
      const d = new Date(cellValue);
      if (isNaN(d.getTime())) return String(cellValue);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: '#e3f2fd', color: '#1565c0', borderRadius: '12px', px: 1.25, py: 0.25, fontSize: '0.8rem', fontWeight: 500 }}>
          <AccessTimeIcon sx={{ fontSize: '13px' }} />
          {`${dd}-${mm}-${yyyy}`}
        </Box>
      );
    } catch {
      return String(cellValue);
    }
  }

  if (INLINE_EDITABLE_CONTROLS.has(controlType)) {
    if (controlType === 'richtext') {
      const plain = typeof cellValue === 'string' ? cellValue.replace(/<[^>]*>/g, '') : '';
      if (!plain) return '-';
      return plain.length > 60 ? plain.slice(0, 60) + '…' : plain;
    }
    if (cellValue === null || cellValue === undefined || cellValue === '') return '-';
    return String(cellValue);
  }

  // Boolean / toggle → disabled Switch
  if (controlType === 'boolean' || controlType === 'toggle') {
    const boolVal = cellValue === true || cellValue === 'true' || cellValue === 1;
    return (
      <Switch checked={boolVal} disabled size="small" color="primary" sx={{ ml: -0.75 }} />
    );
  }

  // Upload → show image thumbnail if it looks like an image URL
  if (controlType === 'upload') {
    if (!cellValue) return '-';
    const rawUrl = String(cellValue);
    // Resolve relative API paths to absolute
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
    const url = rawUrl.startsWith('/api/') ? rawUrl.replace('/api', apiBase) : rawUrl;
    if (isImageUrl(rawUrl)) {
      return (
        <Box
          component="img"
          src={url}
          alt=""
          sx={{
            width: 56,
            height: 56,
            objectFit: 'cover',
            borderRadius: '6px',
            border: '1px solid #E6E7EB',
            display: 'block',
          }}
          onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
        />
      );
    }
    // Non-image file → show filename
    return (
      <Tooltip title={url}>
        <Chip label={url.split('/').pop() || 'file'} size="small" variant="outlined" sx={{ fontSize: '11px', maxWidth: 140 }} />
      </Tooltip>
    );
  }

  // Unknown / complex type
  if (cellValue === null || cellValue === undefined || cellValue === '') return '-';
  if (typeof cellValue === 'object') {
    return (
      <Tooltip title="Edit via modal">
        <Chip label="Support later" size="small" variant="outlined" sx={{ fontSize: '11px' }} />
      </Tooltip>
    );
  }
  return String(cellValue);
};

const collectionHelpers = {
  getEditableCollectionProperties,
  getCollectionSchemaProperties,
  getFieldSchema,
  getFieldTitle,
  getLookupConfig,
  renderLookupCellValue,
  renderCollectionCellInput,
  renderCollectionCellDisplay,
};

export default collectionHelpers;
